// R113 #5 (Jesse: "the row action buttons are cluttered" — nine icons per
// row on the property-details sheet): the ONE shared overflow menu. A row or
// card keeps its single primary inline action and puts everything else
// behind a ⋯ button; the menu opens as an anchored popover (the Calendar's
// .stc-pop look: card surface, --radius, --shadow-pop) and closes on an
// outside click, Esc, a page scroll / resize, or after any item is used. A
// scroll INSIDE the popover (it is overflow:auto, capped at 70vh — a long
// row menu on a short / landscape phone window overflows) keeps it open
// (R113 review #15). Keyboard: the first enabled button takes focus on open
// (never a <select> / <input> row — arrow keys on a focused closed select
// change its value and fire its handler, R113 review #16), ↑/↓ walk the
// buttons (wrapping), Home/End jump, Esc closes; a focused select/input keeps
// its own arrow keys.
//
// Why the menu is rendered INSIDE the page markup (hidden) and only MOVED
// into the popover host on open: every page here binds its handlers with
// body.querySelectorAll('[data-…]') right after an innerHTML redraw. Menu
// items carry exactly the same data-* attributes the old inline buttons
// did, so those handlers keep working untouched — the moved node keeps its
// listeners. On close the node goes back where it came from (or is dropped
// when a redraw already replaced that spot).
//
//   kebabHtml(items, opts)  → the ⋯ button + hidden menu (a string, put it
//                             where the old buttons were)
//   bindKebabs(root)        → ONE delegated listener per root element (safe
//                             to call after every redraw)
//   closeKebab()            → close whatever is open (pages call it before
//                             a redraw they trigger themselves, optional)
import { Formatters } from '../lib/formatters';

export interface KebabItem {
  /** Visible text. Escaped here. */
  label?: string;
  /** A leading glyph in a fixed-width slot (emoji / arrow). Escaped here. */
  icon?: string;
  /** data-* (and any other) attributes the page's own bind() handlers look for — same names as the old inline buttons. */
  attrs?: Record<string, string | number | boolean | null | undefined>;
  title?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Raw row content instead of a button (a <select>, a <label> with a checkbox…). The caller escapes. */
  html?: string;
  /** A thin separator line. */
  divider?: boolean;
}
export interface KebabOpts {
  /** Small caps heading at the top of the menu. */
  title?: string;
  /** The trigger's text (default ⋯). */
  label?: string;
  /** The trigger's tooltip. */
  tip?: string;
  /** Extra classes on the trigger button. */
  cls?: string;
  /** Attributes on the trigger button. */
  attrs?: Record<string, string | number | boolean | null | undefined>;
}

const esc = (s: any) => Formatters.escapeHtml(s == null ? '' : String(s));
const attrStr = (a?: Record<string, any>) => a ? Object.keys(a).filter(k => a[k] !== undefined && a[k] !== null && a[k] !== false).map(k => a[k] === true ? ` ${k}` : ` ${k}="${esc(a[k])}"`).join('') : '';

const CSS_ID = 'sx-kebab-css';
const CSS = `
.kb-btn { min-width:28px; height:26px; padding:0 7px; font-size:15px; line-height:1; font-weight:700; letter-spacing:.05em; border-radius:var(--radius-sm, 8px); flex:none; }
.kb-btn[aria-expanded="true"] { background:var(--accent-soft, #eaf2ff); color:var(--accent, #2563eb); }
.kb-menu[hidden] { display:none !important; }
.kb-pop { position:fixed; z-index:340; min-width:210px; max-width:min(330px, 94vw); background:var(--card, #fff); border:1px solid var(--border, #e5e9f2); border-radius:var(--radius, 16px); box-shadow:var(--shadow-pop, 0 8px 28px rgba(15,23,42,.12)); padding:6px; box-sizing:border-box; max-height:min(70vh, 520px); overflow:auto; overscroll-behavior:contain; }
.kb-pop .kb-menu { display:flex; flex-direction:column; gap:1px; }
.kb-title { font-size:var(--fs-label, 11px); font-weight:700; color:var(--text-faint, #94a3b8); text-transform:uppercase; letter-spacing:.04em; padding:6px 10px 4px; }
.kb-item { display:flex; align-items:center; gap:8px; width:100%; text-align:left; border:none; background:transparent; border-radius:var(--radius-sm, 8px); padding:8px 10px; font:inherit; font-size:var(--fs-body, 12px); font-weight:500; color:var(--text, #0f172a); cursor:pointer; line-height:1.3; }
.kb-item:hover:not([disabled]), .kb-item:focus-visible { background:var(--bg-subtle, #f8fafc); outline:none; }
.kb-item[disabled] { opacity:.45; cursor:not-allowed; }
.kb-item.kb-danger { color:var(--danger-text, #991b1b); }
.kb-item.kb-danger:hover:not([disabled]) { background:var(--danger-soft, #fef2f2); }
.kb-ic { width:18px; text-align:center; flex:none; font-size:12px; }
.kb-row { display:flex; align-items:center; gap:8px; padding:6px 10px; font-size:var(--fs-body, 12px); color:var(--text, #0f172a); }
.kb-row select, .kb-row input[type="text"] { font:inherit; font-size:12px; padding:5px 8px; border:1px solid var(--border, #e5e9f2); border-radius:var(--radius-sm, 8px); background:var(--card, #fff); color:var(--text, #0f172a); max-width:100%; min-width:0; flex:1; }
.kb-row label { display:inline-flex; align-items:center; gap:6px; cursor:pointer; }
.kb-sep { height:1px; background:var(--border-soft, #eef1f6); margin:4px 6px; }
@media (max-width: 640px) { .kb-item { padding:10px 12px; font-size:13px; } }
@media (max-width: 640px), (pointer: coarse) { .kb-btn { min-width:36px; min-height:36px; } .kb-item { min-height:40px; } .kb-row { min-height:40px; } }
`;
// R115 mobile: the trigger is a ≥36px tap target on phones / touch screens.
// This CSS is injected lazily (after main.css), so the size rule lives here.
function ensureCss(): void {
  if (document.getElementById(CSS_ID)) return;
  const st = document.createElement('style');
  st.id = CSS_ID; st.textContent = CSS;
  document.head.appendChild(st);
}

/** The ⋯ trigger plus its hidden menu. Returns '' when there is nothing to show. */
export function kebabHtml(items: KebabItem[], opts: KebabOpts = {}): string {
  const live = (items || []).filter(Boolean);
  if (!live.some(it => !it.divider)) return '';
  const rows = live.map(it => {
    if (it.divider) return '<div class="kb-sep" role="separator"></div>';
    if (it.html) return `<div class="kb-row">${it.html}</div>`;
    return `<button type="button" class="kb-item${it.danger ? ' kb-danger' : ''}" role="menuitem"${it.disabled ? ' disabled' : ''}${it.title ? ` title="${esc(it.title)}"` : ''}${attrStr(it.attrs)}><span class="kb-ic">${esc(it.icon || '')}</span><span>${esc(it.label || '')}</span></button>`;
  }).join('');
  return `<button type="button" class="btn btn-ghost btn-sm kb-btn${opts.cls ? ` ${esc(opts.cls)}` : ''}" data-kebab aria-haspopup="menu" aria-expanded="false" title="${esc(opts.tip || 'More actions')}"${attrStr(opts.attrs)}>${esc(opts.label || '⋯')}</button><div class="kb-menu" role="menu" hidden>${opts.title ? `<div class="kb-title">${esc(opts.title)}</div>` : ''}${rows}</div>`;
}

let open: { host: HTMLElement; menu: HTMLElement; btn: HTMLElement; home: { parent: Node; next: Node | null }; off: () => void } | null = null;

export function closeKebab(): void {
  if (!open) return;
  const o = open; open = null;
  o.off();
  o.btn.setAttribute('aria-expanded', 'false');
  o.menu.setAttribute('hidden', '');
  // Put the menu back where it was rendered so the page's handlers (bound
  // on the items) stay reachable for a reopen; a redraw that already
  // replaced that spot simply drops it (the new markup has a fresh menu).
  if ((o.home.parent as any).isConnected) {
    if (o.home.next && (o.home.next as any).isConnected && o.home.next.parentNode === o.home.parent) o.home.parent.insertBefore(o.menu, o.home.next);
    else o.home.parent.appendChild(o.menu);
  }
  o.host.remove();
}

function openKebab(btn: HTMLElement): void {
  const menu = btn.nextElementSibling as HTMLElement | null;
  if (!menu || !menu.classList.contains('kb-menu')) return;
  if (open && open.btn === btn) { closeKebab(); return; }
  closeKebab();
  ensureCss();
  const home = { parent: menu.parentNode as Node, next: menu.nextSibling };
  const host = document.createElement('div');
  host.className = 'kb-pop';
  host.setAttribute('data-kebab-pop', '');
  host.appendChild(menu);
  menu.removeAttribute('hidden');
  document.body.appendChild(host);
  btn.setAttribute('aria-expanded', 'true');
  // Anchor under the trigger; right-align when the trigger sits in the
  // right half (menus hang off row ends); flip above when there is no room.
  const r = btn.getBoundingClientRect();
  const w = host.offsetWidth, h = host.offsetHeight;
  const vw = window.innerWidth, vh = window.innerHeight;
  let left = r.left + r.width / 2 > vw / 2 ? r.right - w : r.left;
  left = Math.max(8, Math.min(vw - w - 8, left));
  let top = r.bottom + 6;
  if (top + h > vh - 8) top = Math.max(8, r.top - h - 6);
  host.style.left = `${left}px`; host.style.top = `${top}px`;
  const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (host.contains(t) || btn.contains(t)) return; closeKebab(); };
  const items = () => Array.from(menu.querySelectorAll<HTMLElement>('button.kb-item:not([disabled])'));
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); closeKebab(); btn.focus(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    const ae = document.activeElement as HTMLElement | null;
    // Only while focus is in the menu (or still on the trigger); a focused
    // <select> / <input> / <textarea> row keeps its own arrow keys.
    if (!ae || !(host.contains(ae) || ae === btn)) return;
    if (ae.matches('select, input, textarea')) return;
    const list = items();
    if (!list.length) return;
    e.preventDefault(); e.stopPropagation();
    const i = list.indexOf(ae);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? list.length - 1
      : e.key === 'ArrowDown' ? (i < 0 ? 0 : (i + 1) % list.length)
      : (i < 0 ? list.length - 1 : (i - 1 + list.length) % list.length);
    list[next].focus();
  };
  // Scroll (captured at window: it does not bubble) closes the menu only
  // when it is NOT the popover's own overflow scrolling; resize always closes.
  const onAway = (e?: Event) => { if (e && e.target instanceof Node && host.contains(e.target)) return; closeKebab(); };
  // After any item is used, close — on the next tick so the item's own
  // handler (and a <label>'s default action) runs with the node in place.
  const onUse = (e: Event) => { const t = e.target as HTMLElement; if (t.closest('button.kb-item')) setTimeout(closeKebab, 0); };
  const onChange = () => setTimeout(closeKebab, 0);
  const off = () => {
    document.removeEventListener('mousedown', onDoc, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', onAway);
    window.removeEventListener('scroll', onAway, true);
    host.removeEventListener('click', onUse);
    host.removeEventListener('change', onChange);
  };
  open = { host, menu, btn, home, off };
  setTimeout(() => {
    if (open?.host !== host) return;
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onAway);
    window.addEventListener('scroll', onAway, true);
    host.addEventListener('click', onUse);
    host.addEventListener('change', onChange);
  }, 0);
  // Buttons first (a selector LIST returns the first DOM match, not the first
  // selector — a leading <select> row used to take focus); form rows only when
  // there is no button at all.
  (menu.querySelector<HTMLElement>('button.kb-item:not([disabled])') || menu.querySelector<HTMLElement>('select, input') || host).focus?.();
}

/** One delegated opener per root; safe to call after every redraw. */
export function bindKebabs(root: HTMLElement): void {
  if ((root as any).__kbBound) return;
  (root as any).__kbBound = true;
  root.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-kebab]');
    if (!btn || !root.contains(btn)) return;
    e.preventDefault(); e.stopPropagation();
    openKebab(btn);
  });
}
